import { useEffect, useRef } from "react";
import { useHudStore } from "../store/hudStore";
import { canLive, canPasskey } from "../lib/config";
import { pwChecks } from "../lib/auth";
import { Button, Card, Field } from "../design-system/components";
import s from "./Auth.module.css";

function Primary({ busy, busyText, text }: { busy: boolean; busyText: string; text: string }) {
  return (
    <Button type="submit" variant="primary" loading={busy}>
      {busy ? busyText : text}
    </Button>
  );
}

export default function Login() {
  const st = useHudStore();
  const view = st.authView || "signin";
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const previousView = useRef(view);

  // Field errors remain next to their input and move keyboard focus to it.
  useEffect(() => {
    if (!st.authErr) return;
    const field = formRef.current?.elements.namedItem(st.authField);
    if (field instanceof HTMLInputElement) field.focus({ preventScroll: true });
    else errorRef.current?.focus({ preventScroll: true });
  }, [st.authErr, st.authField]);

  useEffect(() => {
    if (previousView.current === view) return;
    previousView.current = view;
    formRef.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, [view]);

  const errFor = (field: string): string | false => st.authField === field && st.authErr;
  const visibleFields = view === "resetConf" ? ["code", "newpass"] : view === "resetReq" ? ["email"] : ["email", "pass"];
  const generalError = st.authErr && !visibleFields.includes(st.authField);
  const submit = view === "resetReq" ? st.sendResetCode : view === "resetConf" ? st.submitReset : st.submitAuth;
  const subtitle =
    view === "resetReq"
      ? "Enter your email and we'll send a reset code."
      : view === "resetConf"
        ? st.resetDest
          ? `Enter the code sent to ${st.resetDest} and a new password.`
          : "Enter the code from your email and a new password."
        : st.email.trim()
          ? "Welcome back. Pick up where you left off."
          : "Sign in to pick up where you left off.";

  return (
    <main className={s.overlay} aria-labelledby="auth-title">
      <Card className={s.card}>
        <header className={s.brand}>
          <span className={s.logo}><img src="/logo.png" alt="" /></span>
          <h1 id="auth-title" className={s.title}>Life OS</h1>
          <p id="auth-subtitle" className={s.subtitle}>{subtitle}</p>
        </header>

        <form
          ref={formRef}
          className={s.form}
          noValidate
          aria-busy={st.authBusy}
          aria-describedby="auth-subtitle"
          onSubmit={(e) => {
            e.preventDefault();
            // Password managers may fill native values without a React change
            // event. Submit what is visibly in the form, then read fresh state.
            const fields = new FormData(e.currentTarget);
            if (view !== "resetConf") st.setEmail(String(fields.get("email") ?? ""));
            if (view === "signin") st.setPass(String(fields.get("pass") ?? ""));
            if (view === "resetConf") { st.setResetCode(String(fields.get("code") ?? "")); st.setResetPass(String(fields.get("newpass") ?? "")); }
            submit();
          }}
        >
          {st.authNote && (
            <div className={s.note} role="status">
              <i className="fa-solid fa-circle-check" aria-hidden="true" />
              {st.authNote}
            </div>
          )}
          {generalError && (
            <div ref={errorRef} className={s.fieldErr} role="alert" tabIndex={-1}>
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              {st.authErr}
            </div>
          )}

          {view !== "resetConf" && (
            <Field
              label="Email address"
              id="auth-email"
              name="email"
              error={errFor("email")}
              value={st.email}
              onChange={(e) => st.setEmail(e.target.value)}
              type="email"
              inputMode="email"
              placeholder="you@example.com"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
            />
          )}

          {view === "resetConf" && (
            <>
              <Field
                label="Reset code"
                id="auth-code"
                name="code"
                className={s.code}
                error={errFor("code")}
                value={st.resetCode}
                onChange={(e) => st.setResetCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
              />
              <div className={s.passwordGroup}>
                <div className={s.passwordField}>
                  <Field
                    label="New password"
                    id="reset-pass"
                    name="newpass"
                    error={errFor("newpass")}
                    aria-describedby="auth-password-requirements"
                    value={st.resetPass}
                    onChange={(e) => st.setResetPass(e.target.value)}
                    type={st.showReset ? "text" : "password"}
                    placeholder="New password"
                    autoComplete="new-password"
                  />
                  <Button variant="quiet" size="compact" className={s.reveal} onClick={st.toggleResetPass} aria-label={st.showReset ? "Hide the new password" : "Show the new password"} aria-pressed={st.showReset} aria-controls="reset-pass">
                    {st.showReset ? "Hide" : "Show"}
                  </Button>
                </div>
                <PwReqs pw={st.resetPass} />
              </div>
            </>
          )}

          {view === "signin" && (
            <div className={s.passwordField}>
              <Field
                label="Password"
                id="signin-pass"
                name="pass"
                error={errFor("pass")}
                value={st.pass}
                onChange={(e) => st.setPass(e.target.value)}
                type={st.showPass ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <Button variant="quiet" size="compact" className={s.reveal} onClick={st.togglePass} aria-label={st.showPass ? "Hide the password" : "Show the password"} aria-pressed={st.showPass} aria-controls="signin-pass">
                {st.showPass ? "Hide" : "Show"}
              </Button>
            </div>
          )}

          <Primary
            busy={st.authBusy}
            busyText={view === "resetReq" ? "Sending…" : view === "resetConf" ? "Resetting…" : "Signing in…"}
            text={view === "resetReq" ? "Send reset code" : view === "resetConf" ? "Reset password" : "Sign in"}
          />
          {view === "signin" && canPasskey() && (
            <>
              <div className={s.divider} aria-hidden="true">
                <span className={s.dividerLine} /><span className={s.dividerText}>or</span><span className={s.dividerLine} />
              </div>
              <Button icon="fa-solid fa-fingerprint" disabled={st.authBusy} onClick={st.passkeySignIn}>
                Sign in with a passkey
              </Button>
            </>
          )}
          {view === "signin" && canLive() && (
            <Button variant="quiet" size="compact" className={s.linkBtn} onClick={st.goReset}>Forgot your password?</Button>
          )}
          {view === "resetConf" && (
            <Button variant="quiet" size="compact" className={s.linkBtn} disabled={st.authBusy} onClick={st.sendResetCode}>Resend code</Button>
          )}
          {view !== "signin" && (
            <Button variant="quiet" size="compact" icon="fa-solid fa-arrow-left" className={s.linkBtn} disabled={st.authBusy} onClick={st.backToSignin}>Back to sign in</Button>
          )}
        </form>

        <span className={s.srOnly} role="status">
          {st.authBusy ? view === "resetReq" ? "Sending reset code." : view === "resetConf" ? "Resetting password." : "Signing in." : ""}
        </span>

        <footer className={s.footer}>
          <p className={s.footerText}><i className="fa-solid fa-lock" aria-hidden="true" />Your notes stay in your own vault.</p>
        </footer>
      </Card>
    </main>
  );
}

function PwReqs({ pw }: { pw: string }) {
  const checks = pwChecks(pw);
  const rows: [boolean, string][] = [[checks.len, "At least 15 characters"], [checks.num, "A number"]];
  return (
    <ul id="auth-password-requirements" className={s.pwReqs} aria-label="Password requirements">
      {rows.map(([ok, text]) => (
        <li key={text} className={s.pwReqRow} data-ok={ok ? "1" : undefined}>
          <i className={`fa-solid ${ok ? "fa-circle-check on" : "fa-circle off"}`} aria-hidden="true" />
          <span className={s.srOnly}>{ok ? "Met: " : "Required: "}</span>{text}
        </li>
      ))}
    </ul>
  );
}
